"use client";

import { JSX, useState } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useTween } from "@/lib/hooks/use-tween";
import { SegmentTabs } from "@/components/motion/segment-tabs";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import type {
  ModuleDualMetricData,
  ModuleDualMetricItem,
  ModuleDualMetricPeriod,
  ValueFormatName,
} from "@/types/modules";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { importsTokenScope } from "./imports-token-scope";
import { formatModuleValue, formatShare } from "./shared/value-format";
import { DUAL_METRIC_TICK_SEGMENTS } from "@/config/layout";

const METRIC_COLORS = ["var(--chart-1)", "var(--chart-3)"] as const;

function TickBar({ value, color, label }: { value: number; color: string; label: string }) {
  const fraction = Math.min(1, Math.max(0, value / 100));
  const filled = fraction === 0 ? 0 : Math.max(1, Math.round(fraction * DUAL_METRIC_TICK_SEGMENTS));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value)}
      className="flex h-4 w-full gap-[3px]"
    >
      {Array.from({ length: DUAL_METRIC_TICK_SEGMENTS }, (_, i) => (
        <span
          key={i}
          className="h-full min-w-0 flex-1 rounded-[1.5px] transition-colors duration-300"
          style={{ background: i < filled ? color : "var(--track)" }}
        />
      ))}
    </div>
  );
}

function MetricValue({ value, format }: { value: number; format?: ValueFormatName }) {
  const shown = useTween(value, { from: 0, duration: 800 });
  const { intlLocale } = useViewerLanguage();
  return (
    <span className="truncate text-3xl font-medium tracking-tight tabular-nums text-[var(--fg)]">
      {formatModuleValue(Math.round(shown), { format, locale: intlLocale })}
    </span>
  );
}

export default function ModuleDualMetric({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleDualMetricData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { intlLocale } = useViewerLanguage();
  const periods = data?.periods ?? [];
  const label = stripInlineMarkdown(data?.label);
  const keyOf = (i: number) => periods[i]?.id ?? String(i);
  const [period, setPeriod] = useState<string>(keyOf(0));
  const currentIndex = Math.max(
    0,
    periods.findIndex((p, i) => keyOf(i) === period),
  );
  const total = (metrics: ModuleDualMetricItem[]) =>
    metrics.reduce((a, m) => a + m.value, 0);

  const renderPeriod = (p: ModuleDualMetricPeriod, i: number, live: boolean) => {
    const metrics = (p.metrics ?? []).slice(0, 2);
    const sum = total(metrics);
    return (
      <div
        key={keyOf(i)}
        aria-hidden={live ? undefined : true}
        className={cn(
          "grid gap-6 [grid-area:1/1] @md:grid-cols-2 @md:gap-0 @md:divide-x @md:divide-[var(--border)]",
          !live && "pointer-events-none invisible",
        )}
      >
        {metrics.map((m, j) => {
          const color = METRIC_COLORS[j] ?? METRIC_COLORS[0];
          const share = sum ? m.value / sum : 0;
          const fill = m.progress ?? share * 100;
          const metricLabel = stripInlineMarkdown(m.label);
          const showShare = m.showShare ?? true;
          return (
            <div
              key={m.id ?? j}
              className="flex min-w-0 flex-col gap-1 @md:px-6 @md:first:pl-0 @md:last:pr-0"
            >
              <div className="flex min-w-0 items-center gap-2">
                {live ? (
                  <MetricValue value={m.value} format={m.format} />
                ) : (
                  <span className="truncate text-3xl font-medium tracking-tight tabular-nums">
                    {formatModuleValue(m.value, { format: m.format, locale: intlLocale })}
                  </span>
                )}
                {showShare && (
                  <span
                    className="shrink-0 rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums"
                    style={{
                      color,
                      background: `color-mix(in oklab, ${color} 14%, transparent)`,
                    }}
                  >
                    {formatShare(share, 0, intlLocale)}
                  </span>
                )}
              </div>
              <span className={cn("text-sm text-[var(--muted)]", MODULE_LABEL_WRAP.cell)}>
                {metricLabel}
              </span>
              <div className="mt-2 flex h-4 items-center">
                <TickBar value={fill} color={color} label={metricLabel} />
              </div>
              {m.detail && (
                <div className="mt-4 flex flex-col gap-0.5">
                  <span className="text-xs text-[var(--subtle)]">
                    {stripInlineMarkdown(m.detail.label)}
                  </span>
                  <span
                    className={cn(
                      "text-sm font-medium tabular-nums text-[var(--fg)]",
                      MODULE_LABEL_WRAP.label,
                    )}
                  >
                    {stripInlineMarkdown(m.detail.value)}
                  </span>
                  {m.detail.description && (
                    <span
                      className={cn(
                        "mt-1 text-xs leading-relaxed text-[var(--muted)]",
                        MODULE_LABEL_WRAP.body,
                      )}
                    >
                      {stripInlineMarkdown(m.detail.description)}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={cn("@container flex w-full flex-col gap-5", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      {(label || periods.length > 1) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {label && <span className="text-sm text-[var(--muted)]">{label}</span>}
          {periods.length > 1 && (
            <SegmentTabs
              value={keyOf(currentIndex)}
              onValueChange={(v) => {
                if (v !== null) setPeriod(v);
              }}
              options={periods.map((p, i) => ({
                value: keyOf(i),
                label: stripInlineMarkdown(p.label),
              }))}
            />
          )}
        </div>
      )}

      <div className="grid">
        {periods.map((p, i) => renderPeriod(p, i, i === currentIndex))}
      </div>
    </div>
  );
}
